/**
 * Display pieces for the "Cart Payment Logs" dialog (Cart and SkillxCart share them).
 *
 * Payment logs keep the amount that was logged when the payment was made, but Cart Edit changes the
 * income records instead. The cart API therefore also returns what income holds now
 * (incomeState / paidAmt / paidDate / otherPayments / receivedTotal). These pieces show that current
 * amount, and the originally logged one underneath when they differ, so the dialog agrees with the
 * Admission Register. Rows that did not get that data (incomeState missing) render exactly as before.
 */
import React from 'react';
import { Alert, Box, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';

export const currencyLabelOf = (item) => (Number(item?.selectedCurrency) === 2 ? 'AED' : 'INR');

export const fmtAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '-';
};

const hasIncomeInfo = (row) => Boolean(row?.incomeState);

const componentsText = (row) =>
  (row.paidComponents || []).map((c) => `${c.feeType} ${fmtAmount(c.amount)}${c.isHold ? ' (hold amount)' : ''}`).join(' + ');

const ORIGINAL_CAPTION_SX = { color: 'warning.dark', display: 'block', lineHeight: 1.3 };

/** "Paid Amount" cell: the amount recorded now, and the originally logged one when it differs. */
export const PaidAmountCell = ({ row }) => {
  if (!hasIncomeInfo(row)) return <>{row.payableAmt}</>;

  if (row.incomeState === 'none') {
    return (
      <Box>
        <Typography variant="body2">{fmtAmount(row.payableAmt)}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
          Awaiting payment
        </Typography>
      </Box>
    );
  }

  if (row.incomeState === 'removed' || row.incomeState === 'elsewhere') {
    return (
      <Box>
        <Typography variant="body2">-</Typography>
        <Typography variant="caption" sx={ORIGINAL_CAPTION_SX}>
          Recorded through cart: {fmtAmount(row.payableAmt)}
        </Typography>
      </Box>
    );
  }

  const parts = (row.paidComponents || []).length > 1 ? componentsText(row) : '';
  const amount = (
    <Typography variant="body2" sx={{ fontWeight: row.amountChanged ? 600 : 400, display: 'inline-block' }}>
      {fmtAmount(row.paidAmt)}
    </Typography>
  );
  return (
    <Box>
      {parts ? (
        <Tooltip title={parts} arrow>
          {amount}
        </Tooltip>
      ) : (
        amount
      )}
      {row.amountChanged ? (
        <Tooltip
          arrow
          title="This payment was changed after it was recorded through the cart (for example in Cart Edit). The amount above is what is recorded now, the same as in the Admission Register."
        >
          <Typography variant="caption" sx={ORIGINAL_CAPTION_SX}>
            Recorded through cart: {fmtAmount(row.payableAmt)}
          </Typography>
        </Tooltip>
      ) : null}
    </Box>
  );
};

/** Date of the payment as recorded now (falls back to the old value for rows without income info). */
export const paymentDateOf = (row) => {
  if (!hasIncomeInfo(row)) return row.transactionDate;
  if (row.incomeState === 'received') return row.paidDate || row.transactionDate;
  return '-';
};

/** Status chip. Existing Completed / Pending behaviour, plus Removed / Other course when income says so. */
export const PaymentStatusChip = ({ row }) => {
  if (row.incomeState === 'removed') {
    return (
      <Tooltip arrow title="Every income entry behind this payment was removed (for example in Cart Edit), so it no longer counts as received.">
        <Chip size="small" variant="outlined" color="error" icon={<Icon icon="mdi:close" />} label="Removed" />
      </Tooltip>
    );
  }
  if (row.incomeState === 'elsewhere') {
    return (
      <Tooltip arrow title="This payment is recorded in income against a different course, so it is not counted for this course.">
        <Chip size="small" variant="outlined" color="warning" icon={<Icon icon="mdi:swap-horizontal" />} label="Other course" />
      </Tooltip>
    );
  }
  const paid = Number(row.paidStatus) === 1;
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={paid ? <Icon icon="mdi:tick" /> : <Icon icon="hugeicons:clock-05" />}
      color={paid ? 'success' : 'warning'}
      label={paid ? 'Completed' : 'Pending'}
    />
  );
};

const needsExplanation = (item) =>
  (item?.otherPayments || []).length > 0 || (item?.CartPaymentLogs || []).some((l) => ['removed', 'elsewhere'].includes(l.incomeState) || l.amountChanged);

/** One short line above the table, only when something on this course differs from what was logged. */
export const PaymentLogsIntro = ({ item }) =>
  needsExplanation(item) ? (
    <Alert severity="info" sx={{ mb: 2 }}>
      Amounts shown are the payments currently recorded for this course, the same figures as the Admission Register. Where a payment was
      changed after it was recorded through the cart, the amount recorded through the cart is shown underneath.
    </Alert>
  ) : null;

/** Income that no payment log covers (added in Cart Edit, hold amounts) + the total received. */
export const OtherPaymentsSection = ({ item }) => {
  const others = item?.otherPayments || [];
  const hasTotal = item?.receivedTotal !== undefined && item?.receivedTotal !== null;
  if (!others.length && !hasTotal) return null;
  const currency = currencyLabelOf(item);
  return (
    <Box sx={{ mt: 3 }}>
      {others.length ? (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Other payments recorded for this course
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            These were added directly (for example in Cart Edit, or as a hold amount), so they have no payment link or receipt.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="other payments">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount ({currency})</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {others.map((p) => (
                  <TableRow key={p.incomeId}>
                    <TableCell>{p.date || '-'}</TableCell>
                    <TableCell>
                      {p.feeType}{' '}
                      {p.isHold ? <Chip size="small" variant="outlined" color="info" label="Hold amount" sx={{ ml: 0.5 }} /> : null}
                    </TableCell>
                    <TableCell>{fmtAmount(p.amount)}</TableCell>
                    <TableCell>{p.mode || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 280, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{p.remarks || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
      {hasTotal ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 1, mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Total received for this course (all payments, including admission fee):
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {currency} {fmtAmount(item.receivedTotal)}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};
